import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useMusic24Capabilities } from '../../../music24/hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { LockChip } from '../../../music24/components/LockedControl/LockedControl';
import { useGetPresaveCampaignQuery } from '../../../../redux/api/fymAPI';
import { CancelCampaignModal } from '../../presave/CancelCampaignModal';
import { SMLSwitch } from '../controls/SMLSwitch';
import { BlockAutosave } from '../forms/useBlockAutosave';
import { catalogLinksMessages as m } from './catalogLinksMessages';
import { useTurnOnPresave } from './useTurnOnPresave';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './CatalogLinks.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogLinksPresaveSwitchProps {
  accountId: number;
  smartLinkId: number;
  widget: SerializedPresaveWidget;
  autosave: BlockAutosave;
}

/**
 * "Let fans pre-save" (design c1, c1b). On a plan with no pre-saves the switch is off and locked; a
 * click opens the plan prompt. On: the campaign is made. Off: the existing cancel dialog.
 */
const CatalogLinksPresaveSwitchComponent: React.FC<CatalogLinksPresaveSwitchProps> = ({
  accountId,
  smartLinkId,
  widget,
  autosave,
}) => {
  const intl = useIntl();
  const { canUsePresave } = useMusic24Capabilities();
  const openUpgrade = useUpgradePrompt();
  const [isCancelOpen, setCancelOpen] = useState(false);
  const hasCampaign = widget.data.hasCampaign !== false && widget.data.campaignUuid !== '';
  const { turnOn, isBusy } = useTurnOnPresave({ accountId, smartLinkId, widget, autosave });
  const { campaign } = useGetPresaveCampaignQuery(
    { accountId, campaignId: widget.data.campaignUuid },
    { skip: !hasCampaign, selectFromResult: ({ data }) => ({ campaign: data?.campaign }) }
  );
  const isLocked = !canUsePresave && !hasCampaign;

  const onChange = useCallback(
    (on: boolean) => {
      if (isLocked) {
        openUpgrade(SMLUpgradeFeature.presave);
        return;
      }
      if (on && !hasCampaign) {
        turnOn();
      } else if (!on && hasCampaign) {
        setCancelOpen(true);
      }
    },
    [hasCampaign, isLocked, openUpgrade, turnOn]
  );
  const unlock = useCallback(() => openUpgrade(SMLUpgradeFeature.presave), [openUpgrade]);
  const closeCancel = useCallback(() => setCancelOpen(false), []);

  return (
    <div className={styles.option}>
      <span className={styles.optionText}>
        <span className={styles.optionTitle}>
          {intl.formatMessage(m.presave)}
          {isLocked && <LockChip feature={SMLUpgradeFeature.presave} />}
        </span>
        <span className={styles.optionSub}>
          {isLocked
            ? intl.formatMessage(m.presaveLockedHelp, { title: widget.data.trackTitle })
            : intl.formatMessage(m.presaveHelp)}
        </span>
        {isLocked && (
          <button type="button" className={styles.change} onClick={unlock}>
            {intl.formatMessage(m.unlock)}
          </button>
        )}
      </span>
      <SMLSwitch
        checked={hasCampaign}
        onChange={onChange}
        disabled={isBusy}
        ariaLabel={intl.formatMessage(m.presave)}
        testId="catalog-presave-switch"
      />
      {campaign !== undefined && (
        <CancelCampaignModal
          isOpen={isCancelOpen}
          closeModal={closeCancel}
          accountId={accountId}
          campaignId={campaign.id}
          campaignUuid={campaign.uuid}
          smartLinkId={smartLinkId}
        />
      )}
    </div>
  );
};

export const CatalogLinksPresaveSwitch = TypedMemo(CatalogLinksPresaveSwitchComponent);
